import React, { useRef } from 'react'

import { Segment, User } from '../types'
import { getCaretPosition } from '../utils/domUtils'
import { parsePastedTextToSegments } from '../utils/pasteUtils'
import { normalizeSegments } from '../utils/segmentUtils'

import { handleKeyDownEditor } from './handleKeyDownEditor'
import { handleInput, handleKeyDownSegment } from './segmentHandlers'
import { UseEditorStateReturn } from './useEditorState'
import { UseMentionLogicReturn } from './useMentionLogic'
import { useNavigation } from './useNavigation'

export interface UseEditorHandlersProps extends UseEditorStateReturn {
  selectAllContent: () => void;
  popupRef: React.RefObject<HTMLDivElement>;
  commitMention: (user: User) => void;
  mentionLogic: UseMentionLogicReturn;
  showPopupForSegment: (segment: Segment) => void;
  requestFocus: () => void;
  isAnnotating?: boolean;
  onFocusChange?: (isFocused: boolean) => void;
}

export const useEditorHandlers = (props: UseEditorHandlersProps) => {
  const { popupRef, commitMention, mentionLogic, showPopupForSegment, isAnnotating, onFocusChange } = props
  const navigation = useNavigation({ ...props, showPopupForSegment })
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handlePaste = (
    e: React.ClipboardEvent<HTMLSpanElement>,
    index: number,
  ) => {
    e.preventDefault()
    const { segments, setSegments, nextId, setActiveIndex, nextCursorPos, users, setIsNavigating } = props
    const pastedText = e.clipboardData.getData('text/plain')
    if (!pastedText) return

    const el = e.currentTarget
    const caretPos = getCaretPosition(el)

    // 1. Split the content of the current segment
    const currentSegment = segments[index]
    const textBefore = currentSegment.content.slice(0, caretPos)
    const textAfter = currentSegment.content.slice(caretPos)

    // 2. Parse the pasted text using the new utility
    const pastedSegments = parsePastedTextToSegments(pastedText, users, nextId)

    // 3. Define the segments that will replace the current one
    const updatedCurrentSegment = { ...currentSegment, content: textBefore }
    const tailSegment: Segment[] = textAfter ? [{ id: `seg-${nextId.current++}`, type: 'text', content: textAfter }] : []

    // 4. If pasting makes the current segment invalid (e.g. splitting a mention), convert it to text
    if (updatedCurrentSegment.type === 'mention' && updatedCurrentSegment.content !== currentSegment.content) {
      updatedCurrentSegment.type = 'text'
    }

    const newSegments = [...segments]
    newSegments.splice(index, 1, updatedCurrentSegment, ...pastedSegments, ...tailSegment)
    const normalized = normalizeSegments(newSegments)
    setSegments(normalized)

    // 5. Set focus and cursor position to the end of the pasted content
    const lastPasted = pastedSegments.length > 0 ? pastedSegments[pastedSegments.length - 1] : updatedCurrentSegment
    const finalNewIndex = normalized.findIndex(s => s.id === lastPasted.id)

    setActiveIndex(finalNewIndex !== -1 ? finalNewIndex : 0)
    
    // The new cursor position will be at the end of what was the last pasted segment's content
    // before it might have been merged with textAfter.
    const newCursorPos = lastPasted.type === 'text'
      ? lastPasted.content.length
      : (pastedSegments.find(s => s.id === lastPasted.id)?.content || '').length

    nextCursorPos.current = newCursorPos
    setIsNavigating(false)
  }

  const handleCut = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const { segments, setSegments, nextId, setActiveIndex, setIsNavigating, nextCursorPos, editorRef } = props
    const selection = window.getSelection()

    if (!selection || selection.isCollapsed || !editorRef.current?.contains(selection.anchorNode)) {
      return
    }

    // 1. Copy to clipboard
    const selectedText = selection.toString()
    navigator.clipboard.writeText(selectedText)

    // 2. Delete the selection from the model
    const range = selection.getRangeAt(0)

    const findSegmentAndIndex = (node: Node): { segment: Segment, index: number } | null => {
      let currentNode: Node | null = node
      while (currentNode && currentNode !== editorRef.current) {
        if (currentNode.nodeType === Node.ELEMENT_NODE && (currentNode as HTMLElement).dataset.segmentId) {
          const segmentId = (currentNode as HTMLElement).dataset.segmentId
          const index = segments.findIndex(s => s.id === segmentId)
          if (index !== -1) {
            return { segment: segments[index], index }
          }
        }
        currentNode = currentNode.parentNode
      }
      return null
    }

    const startInfo = findSegmentAndIndex(range.startContainer)
    const endInfo = findSegmentAndIndex(range.endContainer)

    if (!startInfo || !endInfo) {
      // Fallback to old behavior if we can't identify segments (e.g. selection across editors)
      const newId = `seg-${nextId.current++}`
      setSegments([{ id: newId, type: 'text', content: '' }])
      setActiveIndex(0)
      nextCursorPos.current = 0
      setIsNavigating(false)
      return
    }

    const { segment: startSegment, index: startIndex } = startInfo
    const { index: endIndex } = endInfo
    
    const newSegments = [...segments]

    if (startIndex === endIndex) {
      // Selection is within a single segment
      const newContent = startSegment.content.slice(0, range.startOffset) + startSegment.content.slice(range.endOffset)
      const newType = (startSegment.type === 'mention' && newContent !== startSegment.content) ? 'text' : startSegment.type
      newSegments[startIndex] = { ...startSegment, content: newContent, type: newType }
    } else {
      // Selection spans multiple segments
      const startContent = startSegment.content.slice(0, range.startOffset)
      const newStartType = (startSegment.type === 'mention' && startContent !== startSegment.content) ? 'text' : startSegment.type
      newSegments[startIndex] = { ...startSegment, content: startContent, type: newStartType }

      const endSegment = segments[endIndex]
      const endContent = endSegment.content.slice(range.endOffset)
      const newEndType = (endSegment.type === 'mention' && endContent !== endSegment.content) ? 'text' : endSegment.type
      newSegments[endIndex] = { ...endSegment, content: endContent, type: newEndType }

      // Remove segments in between
      newSegments.splice(startIndex + 1, endIndex - startIndex - 1)
    }
    
    const normalized = normalizeSegments(newSegments)
    setSegments(normalized)

    const finalIndex = normalized.findIndex(s => s.id === startSegment.id)
    if (finalIndex !== -1) {
      setActiveIndex(finalIndex)
      nextCursorPos.current = range.startOffset
    } else {
      // Segment was likely merged. Focus the segment before it.
      const focusIndex = Math.max(0, startIndex - 1)
      const focusSegment = normalized[focusIndex]
      setActiveIndex(focusIndex)
      nextCursorPos.current = focusSegment ? focusSegment.content.length : 0
    }
    
    setIsNavigating(false)
  }

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // This logic is now simplified: it just finds the correct segment and position,
    // and immediately switches to edit mode.
    const selection = window.getSelection()
    if (!selection || !selection.isCollapsed) {
      return
    }

    if (e.target !== e.currentTarget) {
      return
    }

    props.setIsMentionPopupOpen(false)

    const {
      segments,
      setActiveIndex,
      nextCursorPos,
      setIsNavigating,
      setSegments,
      nextId,
      requestFocus,
    } = props

    if (segments.length > 0) {
      const lastIndex = segments.length - 1
      const lastSegment = segments[lastIndex]
      setActiveIndex(lastIndex)
      nextCursorPos.current = lastSegment.content.length
      setIsNavigating(false)
      requestFocus()
    } else {
      const newId = `seg-${nextId.current++}`
      setSegments([{ id: newId, type: isAnnotating ? 'annotate' : 'text', content: '' }])
      setActiveIndex(0)
      nextCursorPos.current = 0
      setIsNavigating(false)
      requestFocus()
    }
  }

  const handleSegmentClick = (
    e: React.MouseEvent<HTMLSpanElement>,
    index: number,
  ) => {
    // We prevent the default browser behavior of placing the caret,
    // because we want to calculate and set it precisely ourselves.
    e.preventDefault()
    e.stopPropagation()

    const selection = window.getSelection()
    // If the user made a selection, do nothing. This handler is for clicks only.
    if (selection && !selection.isCollapsed) {
      return
    }

    // This is the most robust way to get the caret position from a click.
    // We create a new range based on the click coordinates, select it,
    // and then read the offset. This avoids race conditions with the browser's
    // default click handling.
    let newCursorPos = 0
    if (document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY)
      if (range && e.currentTarget.contains(range.startContainer)) {
        selection?.removeAllRanges()
        selection?.addRange(range)
        newCursorPos = range.startOffset
      } else {
        // Fallback for clicks on padding at the end of the element
        newCursorPos = e.currentTarget.textContent?.length || 0
      }
    } else {
      // Fallback for older browsers
      const rect = e.currentTarget.getBoundingClientRect()
      if (e.clientX > rect.left + rect.width / 2) {
        newCursorPos = e.currentTarget.textContent?.length || 0
      }
    }

    const {
      segments,
      setSegments,
      setActiveIndex,
      nextCursorPos,
      setIsNavigating,
      setIsMentionPopupOpen,
      setEditingMentionState,
    } = props
    const segment = segments[index]

    if (segment.type === 'mention') {
      // Enter editing mode for an existing mention
      setEditingMentionState({
        segmentId: segment.id,
        originalContent: segment.content,
      })

      const newSegments = [...segments]
      newSegments[index] = {
        ...newSegments[index],
        type: 'mention_in_progress',
        isModified: true,
      }
      setSegments(newSegments)

      // Use the logic hook to handle the popup
      mentionLogic.updateMention(segment.content)
    } else if (segment.type === 'mention_in_progress') {
      // Just re-trigger the popup logic if already editing
      mentionLogic.updateMention(segment.content)
    } else {
      // If we click on a non-mention segment, ensure the popup is closed.
      setIsMentionPopupOpen(false)
    }

    // Immediately switch to edit mode and set cursor position.
    nextCursorPos.current = newCursorPos
    setActiveIndex(index)
    setIsNavigating(false)
  }

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }
    onFocusChange?.(true)
  }

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    blurTimeoutRef.current = setTimeout(() => {
      // If the focus is moving to the popup, don't do anything.
      if (popupRef.current?.contains(e.relatedTarget as Node)) {
        props.setIsMentionPopupOpen(false)
        return
      }
  
      // When the editor loses focus, we enter navigation mode. This allows the
      // browser to manage the blurred caret state within the main editable container.
      props.setIsNavigating(true)
      props.setIsMentionPopupOpen(false)
      onFocusChange?.(false)
      navigation.cancelSwitchToEditMode()
    }, 0)
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Check if the click is on the mention popup
    const target = e.target as HTMLElement
    const isClickOnMentionPopup = target.closest('.ant-popover') || 
                                  target.closest('[data-mention-popup]') ||
                                  popupRef.current?.contains(target)
    
    if (isClickOnMentionPopup) {
      // Don't switch to navigation mode if clicking on mention popup
      return
    }
    
    // When the user clicks down, we enter navigation mode to allow for selection.
    props.setIsNavigating(true)
    navigation.cancelSwitchToEditMode()
  }

  const handleMouseUp = () => {
    // After the user releases the mouse, if they just clicked (no selection),
    // we should exit navigation mode.
    const selection = window.getSelection()
    if (selection && selection.isCollapsed) {
      props.setIsNavigating(false)
    }
  }

  const onKeyDownEditor = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation()
    navigation.cancelSwitchToEditMode()
    // Navigation logic should only run when in navigation mode.
    if (props.isNavigating && navigation.handleNavigationKeyDown(e)) {
      return
    }

    handleKeyDownEditor(e, props)
  }

  const onInput = (e: React.FormEvent<HTMLSpanElement>, index: number) => {
    handleInput(e, index, props, mentionLogic, isAnnotating)
  }
  const onKeyDownSegment = (
    e: React.KeyboardEvent<HTMLSpanElement>,
    index: number,
  ) => {
    handleKeyDownSegment(e, index, props, mentionLogic, commitMention, isAnnotating)
  }

  return {
    onKeyDownEditor,
    handleContainerClick,
    handleSegmentClick,
    handleFocus,
    handleBlur,
    handlePaste,
    onInput,
    onKeyDownSegment,
    handleMouseDown: handleMouseDown as () => void, // Type assertion for backward compatibility
    handleMouseUp,
    handleCut,
  }
}